import { and, assign, setup } from "xstate";
import type { UserRole } from "./roles";
import { APPROVER_ROLES, type GuardCheck, type WorkActor } from "./roles";
import type { WorkStatus } from "./roles";
import type { CapaPlan, CapaRecord, CapaRootCause, CapaStatus } from "./capa";

/**
 * The CAPA lifecycle, written the same way as `workMachine`: guards are plain
 * functions that return a reason, the machine wraps them, and `explainCapaBlock`
 * runs the same functions so a disabled button and the machine agree.
 *
 * The machine is pure. Facts it cannot see — the investigation record's status,
 * whether every action record has closed, the clock — arrive on the event from
 * `capaStore`, which reads them.
 */

type WithActor = { actor: WorkActor };

export type CapaEvent =
  | ({ type: "START_INVESTIGATION"; investigationRecordId: string } & WithActor)
  | ({
      type: "CONCLUDE_ROOT_CAUSE";
      rootCause: Omit<
        CapaRootCause,
        "concludedBy" | "concludedAt" | "signedOffBy" | "signedOffAt"
      >;
      investigationStatus: WorkStatus | undefined;
      at: string;
    } & WithActor)
  | ({
      type: "SIGN_ROOT_CAUSE";
      decision: "approved" | "changes_requested";
      at: string;
    } & WithActor)
  | ({
      type: "SUBMIT_PLAN";
      plan: Pick<CapaPlan, "actions" | "criteria">;
      at: string;
    } & WithActor)
  | ({
      type: "REVIEW_PLAN";
      decision: "approved" | "changes_requested";
      at: string;
    } & WithActor)
  | ({
      type: "ACTIONS_COMPLETE";
      allActionsClosed: boolean;
      at: string;
      /** Action Work Orders closed without approval — their actions were not done. */
      cancelled?: {
        workRecordId: string;
        reason: string;
        note: string;
        by: string;
      }[];
    } & WithActor)
  | ({ type: "RECURRENCE"; eventId: string } & WithActor)
  | ({ type: "WATCH_PASSED"; at: string } & WithActor)
  | ({
      type: "CLOSE";
      at: string;
      /** Linked Work Orders (investigation and actions) that are not closed yet. */
      openLinkedWork: string[];
    } & WithActor);

export type CapaEventType = CapaEvent["type"];

const PASS: GuardCheck = { ok: true };

/** Plan approval and closure are quality decisions. */
export const QA_ROLES: UserRole[] = ["App Admin", "Lab Manager"];

export const capaChecks = {
  /** An investigation is evidenced work that has been signed off, not an opinion. */
  investigationSignedOff(status: WorkStatus | undefined): GuardCheck {
    if (status === "verifying" || status === "closed") return PASS;
    return {
      ok: false,
      reason:
        "The investigation Work Order must be signed off before a root cause is concluded.",
    };
  },

  hasStatement(statement: string): GuardCheck {
    if (statement.trim().length >= 10) return PASS;
    return {
      ok: false,
      reason: "State the root cause in a sentence the next reader can check.",
    };
  },

  isApprover(actor: WorkActor): GuardCheck {
    if (APPROVER_ROLES.includes(actor.role)) return PASS;
    return { ok: false, reason: `${actor.role} cannot sign off a root cause.` };
  },

  isQa(actor: WorkActor): GuardCheck {
    if (QA_ROLES.includes(actor.role)) return PASS;
    return {
      ok: false,
      reason: `Only a Lab Manager or App Admin can approve a CAPA plan or close a CAPA.`,
    };
  },

  /** The investigator cannot sign off their own conclusion. */
  notInvestigator(capa: CapaRecord, actor: WorkActor): GuardCheck {
    if (!capa.rootCause || capa.rootCause.concludedBy !== actor.name)
      return PASS;
    return {
      ok: false,
      reason: `${actor.name} concluded this root cause and cannot sign it off.`,
    };
  },

  /** Whoever submitted the plan cannot approve it. */
  notPlanSubmitter(capa: CapaRecord, actor: WorkActor): GuardCheck {
    if (!capa.plan.submittedBy || capa.plan.submittedBy !== actor.name)
      return PASS;
    return {
      ok: false,
      reason: `${actor.name} submitted this plan and cannot approve it.`,
    };
  },

  planComplete(plan: Pick<CapaPlan, "actions" | "criteria">): GuardCheck {
    if (!plan.actions.length)
      return { ok: false, reason: "Add at least one action to the plan." };
    const incomplete = plan.actions.find(
      (a) => !a.description.trim() || !a.assignee.trim(),
    );
    if (incomplete)
      return {
        ok: false,
        reason: "Every action needs a description and an assignee.",
      };
    if (!(plan.criteria.windowDays > 0)) {
      return { ok: false, reason: "Set how many days to watch for a repeat." };
    }
    return PASS;
  },

  allActionsClosed(done: boolean): GuardCheck {
    if (done) return PASS;
    return {
      ok: false,
      reason:
        "Every action Work Order must close before the effectiveness watch starts.",
    };
  },

  watchOver(capa: CapaRecord, at: string): GuardCheck {
    if (capa.effectiveness && at >= capa.effectiveness.until) return PASS;
    return {
      ok: false,
      reason: "The effectiveness watch has not run its full window yet.",
    };
  },

  /** Every Work Order the CAPA raised must be closed before the CAPA closes. */
  noOpenLinkedWork(openLinkedWork: string[]): GuardCheck {
    if (!openLinkedWork.length) return PASS;
    return {
      ok: false,
      reason: `${openLinkedWork.join(", ")} ${openLinkedWork.length === 1 ? "is" : "are"} still open — close ${
        openLinkedWork.length === 1 ? "it" : "them"
      } before closing the CAPA.`,
    };
  },

  effective(capa: CapaRecord): GuardCheck {
    if (capa.effectiveness?.result === "passed") return PASS;
    return {
      ok: false,
      reason: "A CAPA closes only after its effectiveness watch passes.",
    };
  },

  /** One more repeat than the plan tolerates reopens the CAPA. */
  exceedsTolerance(capa: CapaRecord): boolean {
    const seen = (capa.effectiveness?.recurrenceEventIds.length ?? 0) + 1;
    return seen > capa.plan.criteria.maxRecurrences;
  },
};

const DAY = 86_400_000;

export const capaMachine = setup({
  types: {
    context: {} as { capa: CapaRecord },
    events: {} as CapaEvent,
    input: {} as { capa: CapaRecord },
  },
  guards: {
    investigationSignedOff: ({ event }) =>
      event.type === "CONCLUDE_ROOT_CAUSE" &&
      capaChecks.investigationSignedOff(event.investigationStatus).ok,
    hasStatement: ({ event }) =>
      event.type === "CONCLUDE_ROOT_CAUSE" &&
      capaChecks.hasStatement(event.rootCause.statement).ok,
    isApprover: ({ event }) => capaChecks.isApprover(event.actor).ok,
    isQa: ({ event }) => capaChecks.isQa(event.actor).ok,
    notInvestigator: ({ context, event }) =>
      capaChecks.notInvestigator(context.capa, event.actor).ok,
    notPlanSubmitter: ({ context, event }) =>
      capaChecks.notPlanSubmitter(context.capa, event.actor).ok,
    planComplete: ({ event }) =>
      event.type === "SUBMIT_PLAN" && capaChecks.planComplete(event.plan).ok,
    allActionsClosed: ({ event }) =>
      event.type === "ACTIONS_COMPLETE" &&
      capaChecks.allActionsClosed(event.allActionsClosed).ok,
    someCancelled: ({ event }) =>
      event.type === "ACTIONS_COMPLETE" && Boolean(event.cancelled?.length),
    watchOver: ({ context, event }) =>
      event.type === "WATCH_PASSED" &&
      capaChecks.watchOver(context.capa, event.at).ok,
    effective: ({ context }) => capaChecks.effective(context.capa).ok,
    noOpenLinkedWork: ({ event }) =>
      event.type === "CLOSE" &&
      capaChecks.noOpenLinkedWork(event.openLinkedWork).ok,
    exceedsTolerance: ({ context }) =>
      capaChecks.exceedsTolerance(context.capa),
    approved: ({ event }) =>
      (event.type === "SIGN_ROOT_CAUSE" || event.type === "REVIEW_PLAN") &&
      event.decision === "approved",
    changesRequested: ({ event }) =>
      (event.type === "SIGN_ROOT_CAUSE" || event.type === "REVIEW_PLAN") &&
      event.decision === "changes_requested",
  },
  actions: {
    setInvestigation: assign(({ context, event }) => {
      if (event.type !== "START_INVESTIGATION") return { capa: context.capa };
      const past = context.capa.investigationRecordId
        ? [
            ...(context.capa.pastInvestigationIds ?? []),
            context.capa.investigationRecordId,
          ]
        : context.capa.pastInvestigationIds;
      return {
        capa: {
          ...context.capa,
          investigationRecordId: event.investigationRecordId,
          pastInvestigationIds: past,
          // A reopened CAPA starts its reasoning again.
          rootCause: undefined,
          effectiveness: undefined,
        },
      };
    }),
    writeRootCause: assign(({ context, event }) => {
      if (event.type !== "CONCLUDE_ROOT_CAUSE") return { capa: context.capa };
      return {
        capa: {
          ...context.capa,
          rootCause: {
            ...event.rootCause,
            concludedBy: event.actor.name,
            concludedAt: event.at,
          },
        },
      };
    }),
    signRootCause: assign(({ context, event }) => {
      if (event.type !== "SIGN_ROOT_CAUSE" || !context.capa.rootCause)
        return { capa: context.capa };
      return {
        capa: {
          ...context.capa,
          rootCause: {
            ...context.capa.rootCause,
            signedOffBy: event.actor.name,
            signedOffAt: event.at,
          },
        },
      };
    }),
    writePlan: assign(({ context, event }) => {
      if (event.type !== "SUBMIT_PLAN") return { capa: context.capa };
      return {
        capa: {
          ...context.capa,
          plan: {
            ...event.plan,
            submittedBy: event.actor.name,
            submittedAt: event.at,
            approvedBy: undefined,
            approvedAt: undefined,
          },
        },
      };
    }),
    approvePlan: assign(({ context, event }) => {
      if (event.type !== "REVIEW_PLAN") return { capa: context.capa };
      return {
        capa: {
          ...context.capa,
          plan: {
            ...context.capa.plan,
            approvedBy: event.actor.name,
            approvedAt: event.at,
          },
        },
      };
    }),
    /**
     * An action closed without approval was not done: the plan goes back to
     * QA. Done actions keep their record; cancelled ones lose it, so approving
     * the plan again raises a fresh Work Order unless QA drops the action.
     */
    returnCancelledToPlanning: assign(({ context, event }) => {
      if (event.type !== "ACTIONS_COMPLETE" || !event.cancelled?.length)
        return { capa: context.capa };
      const byId = new Map(event.cancelled.map((c) => [c.workRecordId, c]));
      return {
        capa: {
          ...context.capa,
          plan: {
            ...context.capa.plan,
            actions: context.capa.plan.actions.map((a) => {
              const c = a.workRecordId ? byId.get(a.workRecordId) : undefined;
              return c
                ? { ...a, workRecordId: undefined, cancelledRecord: c }
                : a;
            }),
            submittedBy: undefined,
            submittedAt: undefined,
            approvedBy: undefined,
            approvedAt: undefined,
          },
        },
      };
    }),
    startEffectiveness: assign(({ context, event }) => {
      if (event.type !== "ACTIONS_COMPLETE") return { capa: context.capa };
      const until = new Date(
        new Date(event.at).getTime() +
          context.capa.plan.criteria.windowDays * DAY,
      ).toISOString();
      return {
        capa: {
          ...context.capa,
          effectiveness: {
            from: event.at,
            until,
            recurrenceEventIds: [],
            result: "watching" as const,
          },
        },
      };
    }),
    noteRecurrence: assign(({ context, event }) => {
      if (event.type !== "RECURRENCE" || !context.capa.effectiveness)
        return { capa: context.capa };
      const ids = [
        ...context.capa.effectiveness.recurrenceEventIds,
        event.eventId,
      ];
      return {
        capa: {
          ...context.capa,
          effectiveness: {
            ...context.capa.effectiveness,
            recurrenceEventIds: ids,
            result:
              ids.length > context.capa.plan.criteria.maxRecurrences
                ? ("failed" as const)
                : ("watching" as const),
          },
        },
      };
    }),
    markPassed: assign(({ context }) => {
      if (!context.capa.effectiveness) return { capa: context.capa };
      return {
        capa: {
          ...context.capa,
          effectiveness: {
            ...context.capa.effectiveness,
            result: "passed" as const,
          },
        },
      };
    }),
    markClosed: assign(({ context, event }) => {
      if (event.type !== "CLOSE") return { capa: context.capa };
      return {
        capa: {
          ...context.capa,
          closedBy: event.actor.name,
          closedAt: event.at,
        },
      };
    }),
  },
}).createMachine({
  id: "capa",
  context: ({ input }) => ({ capa: input.capa }),
  initial: "open",
  states: {
    open: {
      on: {
        START_INVESTIGATION: {
          target: "investigating",
          actions: "setInvestigation",
        },
      },
    },
    investigating: {
      on: {
        CONCLUDE_ROOT_CAUSE: {
          guard: and(["investigationSignedOff", "hasStatement"]),
          target: "root_cause_review",
          actions: "writeRootCause",
        },
      },
    },
    root_cause_review: {
      on: {
        SIGN_ROOT_CAUSE: [
          {
            guard: and(["isApprover", "notInvestigator", "approved"]),
            target: "planning",
            actions: "signRootCause",
          },
          {
            guard: and(["isApprover", "notInvestigator", "changesRequested"]),
            target: "investigating",
          },
        ],
      },
    },
    planning: {
      on: {
        SUBMIT_PLAN: {
          guard: "planComplete",
          target: "plan_review",
          actions: "writePlan",
        },
      },
    },
    plan_review: {
      on: {
        REVIEW_PLAN: [
          {
            guard: and(["isQa", "notPlanSubmitter", "approved"]),
            target: "in_progress",
            actions: "approvePlan",
          },
          {
            guard: and(["isQa", "notPlanSubmitter", "changesRequested"]),
            target: "planning",
          },
        ],
      },
    },
    in_progress: {
      on: {
        ACTIONS_COMPLETE: [
          {
            guard: and(["allActionsClosed", "someCancelled"]),
            target: "planning",
            actions: "returnCancelledToPlanning",
          },
          {
            guard: "allActionsClosed",
            target: "effectiveness_watch",
            actions: "startEffectiveness",
          },
        ],
      },
    },
    effectiveness_watch: {
      on: {
        RECURRENCE: [
          {
            guard: "exceedsTolerance",
            target: "reopened",
            actions: "noteRecurrence",
          },
          { actions: "noteRecurrence" },
        ],
        WATCH_PASSED: { guard: "watchOver", actions: "markPassed" },
        CLOSE: {
          guard: and(["isQa", "effective", "noOpenLinkedWork"]),
          target: "closed",
          actions: "markClosed",
        },
      },
    },
    reopened: {
      on: {
        START_INVESTIGATION: {
          target: "investigating",
          actions: "setInvestigation",
        },
      },
    },
    closed: { type: "final" },
  },
});

/** Why `event` will not land on `capa`, or `null` if it will. Mirrors the machine. */
export function explainCapaBlock(
  capa: CapaRecord,
  event: CapaEvent,
): string | null {
  const s = capa.status;
  const notHere = () =>
    `${event.type.replace(/_/g, " ").toLowerCase()} is not available while this CAPA is ${s.replace(/_/g, " ")}.`;
  const first = (...cs: GuardCheck[]) => {
    const failed = cs.find((c) => !c.ok) as
      { ok: false; reason: string } | undefined;
    return failed ? failed.reason : null;
  };

  switch (event.type) {
    case "START_INVESTIGATION":
      return s === "open" || s === "reopened" ? null : notHere();
    case "CONCLUDE_ROOT_CAUSE":
      if (s !== "investigating") return notHere();
      return first(
        capaChecks.investigationSignedOff(event.investigationStatus),
        capaChecks.hasStatement(event.rootCause.statement),
      );
    case "SIGN_ROOT_CAUSE":
      if (s !== "root_cause_review") return notHere();
      return first(
        capaChecks.isApprover(event.actor),
        capaChecks.notInvestigator(capa, event.actor),
      );
    case "SUBMIT_PLAN":
      if (s !== "planning") return notHere();
      return first(capaChecks.planComplete(event.plan));
    case "REVIEW_PLAN":
      if (s !== "plan_review") return notHere();
      return first(
        capaChecks.isQa(event.actor),
        capaChecks.notPlanSubmitter(capa, event.actor),
      );
    case "ACTIONS_COMPLETE":
      if (s !== "in_progress") return notHere();
      return first(capaChecks.allActionsClosed(event.allActionsClosed));
    case "RECURRENCE":
      return s === "effectiveness_watch" ? null : notHere();
    case "WATCH_PASSED":
      if (s !== "effectiveness_watch") return notHere();
      return first(capaChecks.watchOver(capa, event.at));
    case "CLOSE":
      if (s !== "effectiveness_watch") return notHere();
      return first(
        capaChecks.isQa(event.actor),
        capaChecks.effective(capa),
        capaChecks.noOpenLinkedWork(event.openLinkedWork),
      );
    default:
      return notHere();
  }
}

export const CAPA_MACHINE_STATES: CapaStatus[] = [
  "open",
  "investigating",
  "root_cause_review",
  "planning",
  "plan_review",
  "in_progress",
  "effectiveness_watch",
  "closed",
  "reopened",
];
